import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Button, Card, Col, Input, message, Popconfirm, Popover, Row, Select, Space,
  Statistic, Table, Tag, Tooltip, Typography
} from 'antd'
import {
  AlertOutlined, CheckOutlined, EditOutlined, LeftOutlined,
  RightOutlined, SearchOutlined, VerticalLeftOutlined, VerticalRightOutlined
} from '@ant-design/icons'
import moment from 'moment-timezone'

import { ApiRequest } from '../../../services/api/api'
import { getUserId } from '../../../services/lib/utils/auth.utils'
import OrderApprovedEditDrawer from './order-approved-edit-drawer'
import OrderApprovedEmailModal from './order-approved-email-modal'
import { SplitOptions } from './order-approved-split-modal'

const { Title, Text } = Typography

// 审批页使用的订单结构（由 SalesOrder/GetAllOrder 的数据转换而来）
// 列表只包含待审批（Order Status 4）的订单；审批人等字段接口没返回，暂不显示

export interface ApprovalOrderProduct {
  key: string
  productId: number
  productCode: string
  productName: string
  packQty: number // 每箱数量
  quantity: number // 箱数
  unitPrice: number // 每箱价格
  stockQuantity: number
}

export interface ApprovalOrder {
  orderId: number
  orderNo: string
  custOrderNo?: string
  customer: { customerId: number; customerCode: string; company: string }
  salesName: string
  deliveryMethod: string
  deliveryName: string
  deliveryAddress: string
  deliveryCity: string
  postalCode: string
  orderDate: string
  requiredDate: string
  leadtimeFlag?: boolean // true：REQ Date 由 Order Date + Working Days 计算
  leadtime?: number // Working Days
  deliveryAsap: boolean
  urgentNote?: string
  // 字段名与 sales order 一致：comments = Note to Production，warehouseNote = Delivery Instruction，
  // newWarehouseNote = Note to Warehouse，accountNote = Note to Account
  comments?: string
  warehouseNote?: string
  newWarehouseNote?: string
  accountNote?: string
  paymentTerm: string
  orderStatus: string // sales order 的 Order Status
  createdBy: string
  // GetAllOrder（GetSmallOrderDto）不返回 ParentOrderNo，读不到，先注释掉
  // parentOrderNo?: string // 分单来源（原单号）
  orderProduct: ApprovalOrderProduct[]
  totalPrice: number // 数据库 total_price（含 GST 和 Product Option）
  raw?: any // 接口返回的原始订单，编辑抽屉用
}

const baseGetUrl = 'SalesOrder/GetAllOrder'
const refreshTime = 5

// 订单产品行的 key，分单弹窗按它记录移到新单的数量
const orderProductKey = (row: any, item: any) => String(item.orderProductId ?? `${row.orderId}-${item.productId}`)

// 以下与 Order Edit 弹窗的算法一致：行价 = 单价 × 数量，GST 15%。
// AddOnlineOrder / UpdateOnlineOrder 直接保存前端传的金额，所以分单时需要前端算
const round2 = (n: number) => Math.round(n * 100) / 100

const toProductLine = (item: any, quantity: number) => ({
  productId: item.productId,
  quantity,
  unitPrice: item.unitPrice,
  price: round2((item.unitPrice || 0) * quantity),
  marginOfError: item.marginOfError
})

const toOptionLine = (o: any) => ({ optionId: o.optionId, quantity: o.quantity, unitPrice: o.unitPrice, price: o.price })

const withTotals = (orderProduct: any[], orderOption: any[]) => {
  const subtotal = orderProduct.reduce((sum, p) => sum + (p.price || 0), 0)
    + orderOption.reduce((sum, o) => sum + (o.price || 0), 0)
  const priceInclgst = round2(subtotal * 0.15)
  return { orderProduct, orderOption, priceInclgst, totalPrice: round2(subtotal + priceInclgst) }
}

// 表头颜色与 common-table 实际显示的一致（common-table 表头单元格被 material-table 刷成白色，
// 看到的绿色是 toolbar：#dcecc3 → #f1f8e9，底边 #689f38）
// 用纯色：渐变加在每个 th 上会逐列重复出现条纹
const GreenHeaderCell = (props: any) => (
  <th
    {...props}
    style={{
      ...props.style,
      background: '#dcecc3',
      color: '#1A3C34',
      fontWeight: 600,
      fontSize: '1rem',
      borderBottom: '2px solid #689f38'
    }}
  />
)

// 操作列 icon（不用 Button：全局 .ant-btn padding 会挤掉 icon-only 按钮里的图标）
const actionIconColors: Record<string, { color: string; bg: string; hoverBg: string }> = {
  edit: { color: '#1A3C34', bg: '#f1f8e9', hoverBg: '#8bc34a' },
  approve: { color: '#389e0d', bg: '#f6ffed', hoverBg: '#52c41a' }
}

// 需要透传 props，Tooltip / Popconfirm 靠它们绑定鼠标事件
const ActionIcon = ({ type, children, onClick, onMouseEnter, onMouseLeave, ...rest }: any) => {
  const [hover, setHover] = useState(false)
  const c = actionIconColors[type]
  return (
    <span
      {...rest}
      role="button"
      onClick={onClick}
      onMouseEnter={(e) => { setHover(true); onMouseEnter?.(e) }}
      onMouseLeave={(e) => { setHover(false); onMouseLeave?.(e) }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 30,
        height: 30,
        borderRadius: 8,
        fontSize: 16,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        color: hover ? '#ffffff' : c.color,
        background: hover ? c.hoverBg : c.bg
      }}
    >
      {children}
    </span>
  )
}

// 表格底部分页，样式与 common-table 的 material-table 分页栏一致（stepped）
const pageSizeOptions = [5, 10, 30, 50, 100]

const PagerIcon = (props: { icon: React.ReactNode; disabled: boolean; onClick: () => void }) => (
  <span
    role="button"
    onClick={() => !props.disabled && props.onClick()}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 32,
      height: 32,
      fontSize: 16,
      color: props.disabled ? 'rgba(26, 60, 52, 0.3)' : '#1A3C34',
      cursor: props.disabled ? 'default' : 'pointer'
    }}
  >
    {props.icon}
  </span>
)

const TablePagerFooter = (props: {
  total: number
  page: number
  pageSize: number
  onChange: (page: number, pageSize: number) => void
}) => {
  const { total, page, pageSize, onChange } = props
  const pageCount = Math.max(1, Math.ceil(total / pageSize))
  // 当前页附近最多显示 5 个页码
  const start = Math.max(1, Math.min(page - 2, pageCount - 4))
  const pages = Array.from({ length: Math.min(5, pageCount) }, (_, i) => start + i)

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 8,
        minHeight: 56,
        padding: '0 24px',
        background: 'linear-gradient(135deg, #dcecc3 0%, #f1f8e9 100%)',
        borderTop: '1px solid #e8f5e9',
        borderRadius: '0 0 8px 8px'
      }}
    >
      <Select
        bordered={false}
        value={pageSize}
        onChange={(v: number) => onChange(1, v)}
        style={{ width: 110, color: '#558b2f', marginRight: 16 }}
        options={pageSizeOptions.map(n => ({ label: `${n} rows`, value: n }))}
      />
      <PagerIcon icon={<VerticalRightOutlined />} disabled={page <= 1} onClick={() => onChange(1, pageSize)} />
      <PagerIcon icon={<LeftOutlined />} disabled={page <= 1} onClick={() => onChange(page - 1, pageSize)} />
      {pages.map(p => (
        <span
          key={p}
          role="button"
          onClick={() => onChange(p, pageSize)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 28,
            height: 28,
            padding: '0 6px',
            borderRadius: 6,
            fontSize: 13,
            cursor: 'pointer',
            color: p === page ? 'rgba(0, 0, 0, 0.6)' : '#1A3C34',
            background: p === page ? 'rgba(0, 0, 0, 0.1)' : 'transparent'
          }}
        >
          {p}
        </span>
      ))}
      <PagerIcon icon={<RightOutlined />} disabled={page >= pageCount} onClick={() => onChange(page + 1, pageSize)} />
      <PagerIcon icon={<VerticalLeftOutlined />} disabled={page >= pageCount} onClick={() => onChange(pageCount, pageSize)} />
    </div>
  )
}

const OrderApprovedPage = () => {
  const [orders, setOrders] = useState<ApprovalOrder[]>([])
  const [keyword, setKeyword] = useState('')
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [activeOrderId, setActiveOrderId] = useState<number | null>(null)
  // 审批成功、待发确认邮件的订单；队首就是当前弹出的邮件窗口，发送或取消后弹下一封
  const [emailQueue, setEmailQueue] = useState<ApprovalOrder[]>([])
  const [emailTotal, setEmailTotal] = useState(0)
  const emailOrder = emailQueue[0] || null
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(100)

  // 后端返回 UTC 时间，转成 NZ 本地日期（同 weekly attendance）
  const formatDate = (date: any) => (date ? moment.utc(date).tz('Pacific/Auckland').format('YYYY-MM-DD') : '')

  const getRenderData = (data: any[]) => {
    const renderData: ApprovalOrder[] = []
    data.forEach((row: any) => {
      // 只显示待审批（Order Status 4）的订单
      if (row.orderStatus?.orderStatusId !== 4) return
      renderData.push({
        ...row,
        custOrderNo: row.custOrderNo || undefined,
        customer: {
          customerId: row.customer?.customerId ?? row.customerId,
          customerCode: row.customer?.customerCode || '',
          company: row.customer?.company || ''
        },
        salesName: [row.employee?.firstName, row.employee?.lastName].filter(Boolean).join(' '),
        deliveryMethod: row.deliveryMethod?.deliveryMethodName || '',
        deliveryCity: row.deliveryCity?.cityName || '',
        orderDate: formatDate(row.orderDate),
        requiredDate: formatDate(row.requiredDate),
        deliveryAsap: !!row.deliveryAsap,
        paymentTerm: row.customer?.paymentCycle?.paymentCycleName || '',
        orderStatus: row.orderStatus?.orderStatusName || '',
        createdBy: row.createdEmployee?.firstName
          ? row.createdEmployee.firstName.charAt(0).toUpperCase() + row.createdEmployee.firstName.slice(1)
          : 'Online',
        orderProduct: (row.orderProduct || []).map((item: any) => ({
          key: orderProductKey(row, item),
          productId: item.productId,
          productCode: item.product?.productCode || '',
          productName: item.product?.productName || '',
          packQty: item.product?.baseProduct?.packagingType?.quantity || 0,
          quantity: item.quantity || 0,
          unitPrice: item.unitPrice || 0,
          stockQuantity: item.stockQuantity ?? item.product?.stockQuantity ?? 0
        })),
        totalPrice: row.totalPrice || 0,
        // GetSmallOrderDto 不返回 ParentOrderNo，先注释掉
        // parentOrderNo: row.parentOrderNo || undefined,
        raw: row
      })
    })
    return renderData
  }

  // 只取最近 3 个月：start = 3 个月前当天 0 点，end 往后延一天（确保包含今天的订单）；本地时间转 UTC 后按 YYYY/MM/DD 传
  const loadOrders = () => {
    const url = baseGetUrl + '?start=' + moment().add(-3, 'months').startOf('day').utc().format('YYYY/MM/DD')
      + '&end=' + moment().add(1, 'days').endOf('day').utc().format('YYYY/MM/DD')
    ApiRequest({ url, method: 'get' }).then((res: any) => {
      setOrders(getRenderData(res?.data?.data || []))
      setSelectedRowKeys([])
    })
  }

  useEffect(() => {
    document.title = 'Order Approval'
    loadOrders()
  }, [])

  // 抽屉 / 邮件弹窗打开时不刷新，避免正在编辑的订单被覆盖
  const openRef = useRef(false)
  openRef.current = activeOrderId !== null || emailOrder !== null
  // 防止审批请求进行中重复点击
  const approvingRef = useRef(false)

  // 每 5 分钟刷新一次数据
  useEffect(() => {
    const timer = setInterval(() => {
      if (!openRef.current) {
        loadOrders()
      }
    }, refreshTime * 60000)
    return () => clearInterval(timer)
  }, [])

  const activeOrder = orders.find(o => o.orderId === activeOrderId) || null

  const filteredOrders = useMemo(() => {
    const kw = keyword.trim().toLowerCase()
    return orders.filter(o => {
      if (!kw) return true
      return [
        o.orderNo, o.custOrderNo, o.customer.customerCode, o.customer.company,
        ...o.orderProduct.map(p => p.productCode), ...o.orderProduct.map(p => p.productName)
      ].some(s => s?.toLowerCase().includes(kw))
    })
  }, [orders, keyword])

  // 筛选后总页数变少时，回到有效页
  useEffect(() => {
    const pageCount = Math.max(1, Math.ceil(filteredOrders.length / pageSize))
    if (page > pageCount) setPage(pageCount)
  }, [filteredOrders.length, pageSize, page])

  const stats = useMemo(() => ({
    pending: orders.length,
    pendingAmount: orders.reduce((sum, o) => sum + o.totalPrice, 0)
  }), [orders])

  // 抽屉里保存走的是真实接口（UpdateOrder / UpdateFullOrder），保存后重新拉数据
  const handleSaved = () => {
    setActiveOrderId(null)
    loadOrders()
  }

  // 逐张调用审批接口。失败原因由全局拦截器弹窗显示，这里关掉每张成功后的自动提示，最后统一汇总
  const approve = async (list: ApprovalOrder[]) => {
    if (approvingRef.current) return
    approvingRef.current = true
    const hide = message.loading(`Approving ${list.length} order(s)…`, 0)
    const failed: string[] = []
    const approved: ApprovalOrder[] = []
    try {
      for (const o of list) {
        try {
          await ApiRequest({
            url: `OrderFromCustomer/ApproveOnlineOrder?id=${o.orderId}&employeeId=${getUserId()}`,
            method: 'put',
            isNotShowSpinnerOverride: true
          })
          approved.push(o)
        } catch (e) {
          failed.push(o.orderNo)
        }
      }
    } finally {
      hide()
      approvingRef.current = false
    }
    const succeeded = list.length - failed.length
    if (succeeded) message.success(list.length === 1 ? `${list[0].orderNo} approved` : `${succeeded} order(s) approved`)
    if (failed.length) message.error(`Failed to approve: ${failed.join(', ')}`)
    // 审批成功的订单逐封弹出确认邮件窗口。重新拉数据后它们会从列表消失，所以队列里存的是订单对象本身
    if (approved.length) {
      setEmailQueue(approved)
      setEmailTotal(approved.length)
    }
    setActiveOrderId(null)
    loadOrders()
  }

  // 分单：AddOnlineOrder 建新单 → UpdateOnlineOrder 扣减原单 → 第二步失败则 DeleteOrder 回滚新单
  const splitOrder = async (order: ApprovalOrder, options: SplitOptions) => {
    const raw = order.raw
    const employeeId = getUserId()
    const moved = (item: any) => Math.min(options.moves[orderProductKey(raw, item)] || 0, item.quantity)

    const newLines = raw.orderProduct.filter((item: any) => moved(item) > 0).map((item: any) => toProductLine(item, moved(item)))
    const remainLines = raw.orderProduct
      .map((item: any) => toProductLine(item, item.quantity - moved(item)))
      .filter((line: any) => line.quantity > 0)

    // 新单复制原单的客户、销售、PO、地址和备注；REQ Date / 配送方式 / 加急 / Product Option 用分单弹窗里填的
    const newOrder = {
      customerId: raw.customerId,
      employeeId: raw.employeeId,
      orderDate: raw.orderDate,
      requiredDate: moment(options.requiredDate, 'YYYY-MM-DD').toISOString(),
      deliveryName: raw.deliveryName,
      deliveryAddress: raw.deliveryAddress,
      postalCode: raw.postalCode,
      deliveryCityId: raw.deliveryCityId,
      deliveryMethodId: options.deliveryMethodId,
      deliveryAsap: options.deliveryAsap ? 1 : 0,
      urgentNote: options.deliveryAsap ? options.urgentNote : null,
      custOrderNo: raw.custOrderNo,
      comments: raw.comments,
      productionNoteType: raw.productionNoteType,
      warehouseNote: raw.warehouseNote,
      warehouseNoteType: raw.warehouseNoteType,
      newWarehouseNote: raw.newWarehouseNote,
      accountNote: raw.accountNote,
      accountNoteType: raw.accountNoteType,
      leadtimeFlag: 0, // REQ Date 是分单时手动选的
      operEmployeeId: employeeId,
      createdEmployeeId: employeeId,
      parentOrderNo: order.orderNo,
      ...withTotals(newLines, options.newOptions)
    }

    // 原单只扣减产品数量，其余字段（含 Product Option）原样传回
    const originalOrder = {
      orderId: raw.orderId,
      customerId: raw.customerId,
      employeeId: raw.employeeId,
      orderDate: raw.orderDate,
      requiredDate: raw.requiredDate,
      deliveryDate: raw.deliveryDate,
      deliveryName: raw.deliveryName,
      deliveryAddress: raw.deliveryAddress,
      postalCode: raw.postalCode,
      comments: raw.comments,
      deliveryAsap: raw.deliveryAsap,
      deliveryCityId: raw.deliveryCityId,
      deliveryMethodId: raw.deliveryMethodId,
      custOrderNo: raw.custOrderNo,
      accountNote: raw.accountNote,
      accountNoteType: raw.accountNoteType,
      warehouseNote: raw.warehouseNote,
      warehouseNoteType: raw.warehouseNoteType,
      urgentNote: raw.urgentNote,
      // 读不到原单的 ParentOrderNo，先注释掉
      // parentOrderNo: raw.parentOrderNo,
      ...withTotals(remainLines, (raw.orderOption || []).map(toOptionLine))
    }

    let newOrderId: string | undefined
    try {
      const res = await ApiRequest({ url: 'OrderFromCustomer/AddOnlineOrder', method: 'post', data: newOrder, isNotShowSpinnerOverride: true })
      newOrderId = res?.data?.data
    } catch (e) {
      return // 失败原因由全局拦截器弹窗显示，什么都没改
    }

    try {
      await ApiRequest({ url: 'OrderFromCustomer/UpdateOnlineOrder', method: 'put', data: originalOrder, isNotShowSpinnerOverride: true })
      message.success(`${order.orderNo} split into a new order`)
    } catch (e) {
      // 原单没改成功，删掉刚建的新单，避免数量重复
      try {
        await ApiRequest({ url: `SalesOrder/DeleteOrder?id=${newOrderId}`, method: 'delete', isNotShowSpinnerOverride: true })
        message.error(`Split failed, ${order.orderNo} is unchanged`)
      } catch (deleteError) {
        message.error(`Split failed and the new order could not be removed. Please delete the new order split from ${order.orderNo} manually.`, 10)
      }
    }
    setActiveOrderId(null)
    loadOrders()
  }

  const selectedOrders = orders.filter(o => selectedRowKeys.includes(o.orderId))

  const columns: any[] = [
    {
      title: 'Order No',
      dataIndex: 'orderNo',
      width: 150,
      render: (v: string, row: ApprovalOrder) => (
        <div>
          <a onClick={() => setActiveOrderId(row.orderId)}><b>{v}</b></a>
          {/* GetAllOrder 不返回 ParentOrderNo，先注释掉
          {row.parentOrderNo && (
            <div><Text type="secondary" style={{ fontSize: 12 }}>Split from {row.parentOrderNo}</Text></div>
          )} */}
        </div>
      )
    },
    {
      title: 'PO No',
      dataIndex: 'custOrderNo',
      width: 140,
      // Tag 默认不换行，超出列宽时会盖到下一列
      render: (v?: string) => v && (
        <Tag color="orange" style={{ margin: 0, maxWidth: '100%', whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{v}</Tag>
      )
    },
    {
      title: 'Customer',
      dataIndex: ['customer', 'customerCode'],
      width: 180
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      width: 110,
      render: (v: string) => <Tag color="cyan">{v}</Tag>
    },
    {
      title: 'Sale',
      dataIndex: 'salesName',
      width: 180,
    },
    {
      title: 'Delivery',
      dataIndex: 'deliveryMethod',
      width: 140,
      render: (v: string, row: ApprovalOrder) => (
        <div>
          <Tag color="purple">{v}</Tag>
          <div><Text type="secondary" style={{ fontSize: 12 }}>{row.deliveryCity}</Text></div>
        </div>
      )
    },
    {
      title: 'Items',
      dataIndex: 'orderProduct',
      render: (items: ApprovalOrder['orderProduct']) => (
        <div>
          {items.map(p => (
            <div key={p.key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
              <Tag color="processing" style={{ margin: 0, fontFamily: 'monospace', fontWeight: 600, minWidth: 64, textAlign: 'center' }}>
                {p.productCode}
              </Tag>
              <Tooltip title={p.productName}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.productName}</span>
              </Tooltip>
              <Tag color="blue" style={{ margin: 0, marginLeft: 'auto', flexShrink: 0 }}>{p.quantity} × {p.packQty}</Tag>
            </div>
          ))}
        </div>
      )
    },
    {
      title: 'Total',
      width: 110,
      align: 'right',
      dataIndex: 'totalPrice',
      sorter: (a: ApprovalOrder, b: ApprovalOrder) => a.totalPrice - b.totalPrice,
      render: (v: number) => <b>${v.toFixed(2)}</b>
    },
    {
      title: 'Order Date',
      dataIndex: 'orderDate',
      width: 100,
      sorter: (a: ApprovalOrder, b: ApprovalOrder) => a.orderDate.localeCompare(b.orderDate),
      render: (v: string) => moment(v).format('DD/MM/YY')
    },
    {
      title: 'REQ Date',
      dataIndex: 'requiredDate',
      width: 100,
      defaultSortOrder: 'ascend',
      sorter: (a: ApprovalOrder, b: ApprovalOrder) => a.requiredDate.localeCompare(b.requiredDate),
      render: (v: string, row: ApprovalOrder) => (
        <div>
          {row.deliveryAsap && (
            <Popover content={row.urgentNote || 'ASAP'} title={<div style={{ textAlign: 'center' }}>Urgent</div>}>
              <AlertOutlined style={{ fontSize: 18, color: 'red' }} />
            </Popover>
          )}
          <div>{moment(v).format('DD/MM/YY')}</div>
        </div>
      )
    },
    {
      title: 'Actions',
      width: 100,
      fixed: 'right',
      render: (_: any, row: ApprovalOrder) => (
        <Space size={6}>
          <Tooltip title="Edit">
            <ActionIcon type="edit" onClick={() => setActiveOrderId(row.orderId)}><EditOutlined /></ActionIcon>
          </Tooltip>
          <Popconfirm title={`Approve ${row.orderNo}?`} onConfirm={() => approve([row])}>
            <Tooltip title="Approve">
              <ActionIcon type="approve"><CheckOutlined /></ActionIcon>
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ]

  const statCard = (title: string, value: any, color?: string, extra?: any) => (
    <Card size="small" bodyStyle={{ padding: '12px 16px' }}>
      <Statistic title={title} value={value} valueStyle={{ color }} {...extra} />
    </Card>
  )

  return (
    <div style={{ padding: 16 }}>
      <div style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>Order Approval</Title>
        <Text type="secondary">Review and edit incoming orders. Only approved orders become sales orders.</Text>
      </div>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} md={12}>{statCard('Pending Approval', stats.pending, '#d48806')}</Col>
        <Col xs={12} md={12}>{statCard('Pending Amount', stats.pendingAmount, undefined, { precision: 2, prefix: '$' })}</Col>
      </Row>

      <Card size="small" bodyStyle={{ padding: 12 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between', marginBottom: 12 }}>
          <Input allowClear placeholder="Order / customer / product" style={{ width: 280 }}
                 prefix={<SearchOutlined style={{ color: '#8c8c8c' }} />}
                 onChange={(e) => setKeyword(e.target.value)} />
          <Space>
            {selectedOrders.length > 0 && <Text type="secondary">{selectedOrders.length} selected</Text>}
            <Popconfirm title={`Approve ${selectedOrders.length} selected order(s)?`} disabled={!selectedOrders.length}
                        onConfirm={() => approve(selectedOrders)}>
              <Button type="primary" icon={<CheckOutlined />} disabled={!selectedOrders.length}>
                Approve Selected
              </Button>
            </Popconfirm>
          </Space>
        </div>

        <Table
          rowKey="orderId"
          components={{ header: { cell: GreenHeaderCell } }}
          size="middle"
          columns={columns}
          dataSource={filteredOrders}
          scroll={{ x: 1440 }}
          // 用 antd 自带分页切片（排序对全部数据生效），隐藏默认分页器，改用下方自定义底栏
          pagination={{ current: page, pageSize, position: ['none' as any] }}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys)
          }}
          onRow={(row) => ({ onDoubleClick: () => setActiveOrderId(row.orderId) })}
        />
        <TablePagerFooter
          total={filteredOrders.length}
          page={page}
          pageSize={pageSize}
          onChange={(p, size) => { setPage(p); setPageSize(size) }}
        />
      </Card>

      <OrderApprovedEditDrawer
        order={activeOrder}
        visible={!!activeOrder}
        onClose={() => setActiveOrderId(null)}
        onSaved={handleSaved}
        onApprove={(o) => approve([o])}
        onSplit={splitOrder}
      />

      {/* key 按订单区分：换下一封时重新挂载，表单初始值（Receiver / Subject）才会更新 */}
      <OrderApprovedEmailModal
        key={emailOrder?.orderId}
        order={emailOrder}
        visible={!!emailOrder}
        progress={emailTotal > 1 ? `${emailTotal - emailQueue.length + 1}/${emailTotal}` : undefined}
        onCancel={() => setEmailQueue(q => q.slice(1))}
      />
    </div>
  )
}

export default OrderApprovedPage
